// src/pages/Connector/ServiceNow/ListServiceNowTickets.js - CHATBOT CONTEXT VERSION
// Redesigned with Material UI styling to match ListUsers design

import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import DataTable from "react-data-table-component";
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  useTheme,
} from "@mui/material";
import TicketAnalysisModal from "./TicketAnalysisModal";

export default function ListServiceNowTickets({ refreshTrigger, chatbotId, permission }) {
  const theme = useTheme();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [ticketType, setTicketType] = useState("incident");

  const fetchTickets = useCallback(
    async (page = currentPage, pageSize = perPage, search = searchText, type = ticketType) => {
      setLoading(true);
      setError(null);

      try {
        const params = {
          page: page,
          per_page: pageSize,
          search: search,
          type: type
        };

        // Add chatbot_id if available
        if (chatbotId) {
          params.chatbot_id = chatbotId;
        }

        const response = await axiosInstance.get(`/api/servicenow/tickets`, { params });

        if (response.data.success) {
          setTickets(response.data.data);
          setTotalRows(response.data.pagination.total);
        } else {
          setTickets([]);
          setError(response.data.message || "Failed to fetch tickets");
        }
      } catch (err) {
        setTickets([]);
        console.error("Error fetching ServiceNow tickets:", err);
        setError(err.response?.data?.message || "Error fetching tickets.");
      } finally {
        setLoading(false);
      }
    },
    [currentPage, perPage, searchText,  chatbotId, ticketType]
  );

  useEffect(() => {
    fetchTickets();
  }, [refreshTrigger, ticketType]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchText !== undefined) {
        setCurrentPage(1);
        fetchTickets(1, perPage, searchText);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchText]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchTickets(page, perPage, searchText);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
    fetchTickets(page, newPerPage, searchText);
  };

  const handleSyncTickets = async () => {
    if (!chatbotId) {
      setError("Please select a chatbot first");
      return;
    }

    setIsSyncing(true);
    setError(null);

    try {
      const response = await axiosInstance.post("/api/servicenow/tickets/sync", null, {
        params: {
          chatbot_id: chatbotId,
          type: ticketType,
          limit: 50,
        },
      });
      if (response.data.success) {
        setError(`Successfully synced! ${response.data.message}`);
        fetchTickets();
      } else {
        setError(response.data.message || "Failed to sync tickets");
      }
    } catch (err) {
      console.error("Error syncing tickets:", err);
      setError(err.response?.data?.message || "Error syncing tickets.");
    } finally {
      setIsSyncing(false);
    }
  };

  const handleAnalyzeTicket = (ticket) => {
    setSelectedTicket(ticket);
    setIsAnalysisModalOpen(true);
  };

  const handleDeleteTicket = async (ticketId, ticketNumber) => {
    if (
      !window.confirm(
        `Are you sure you want to delete ticket ${ticketNumber}? This action cannot be undone.`
      )
    ) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      await axiosInstance.delete(`/api/servicenow/tickets/${ticketId}`);
      setError("Ticket deleted successfully.");
      fetchTickets();
    } catch (err) {
      console.error(`Error deleting ticket ${ticketId}:`, err);
      setError(err.response?.data?.message || `Error deleting ticket.`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleModalClose = () => {
    setIsAnalysisModalOpen(false);
    setSelectedTicket(null);
    fetchTickets(); // Refresh to show updated analysis status
  };

  
  const handleTicketType = (type) => {
    setTicketType(type);
    setCurrentPage(1); // Refresh to show updated analysis status
  };

  const getPriorityChip = (priority) => {
    const priorityMap = {
      "1": { label: "Critical", color: "error" },
      "2": { label: "High", color: "warning" },
      "3": { label: "Moderate", color: "info" },
      "4": { label: "Low", color: "default" },
      "5": { label: "Planning", color: "default" },
    };

    const p = priorityMap[priority] || { label: priority, color: "default" };
    return <Chip label={p.label} color={p.color} size="small" />;
  };

  const getStateChip = (state) => {
    const stateMap = {
      New: "primary",
      "In Progress": "info",
      "On Hold": "warning",
      Resolved: "success",
      Closed: "default",
      Cancelled: "default",
    };

    const chipColor = stateMap[state] || "default";
    return <Chip label={state} color={chipColor} size="small" />;
  };

  // ✅ Custom MUI-based DataTable styling for dark mode
  const customDataTableStyles = {
    table: {
      style: {
        backgroundColor: theme.palette.background.paper,
      },
    },
    headRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
        color: theme.palette.text.primary,
        borderBottom: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#cbd5e1"
        }`,
      },
    },
    headCells: {
      style: {
        fontWeight: 600,
        fontSize: "14px",
        color: theme.palette.text.primary,
      },
    },
    rows: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
        "&.rdt_Row:hover": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#131b2b !important" : "#f8fafc",
        },
        "&.rdt_Row--selected": {
          backgroundColor:
            theme.palette.mode === "dark" ? "#131b2b !important" : "#e2e8f0",
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderTop: `1px solid ${
          theme.palette.mode === "dark" ? "#334155" : "#e2e8f0"
        }`,
      },
    },
    expanderRow: {
      style: {
        backgroundColor: theme.palette.mode === "dark" ? "#0f172a" : "#f9fafb",
      },
    },
  };

  const columns = [
    {
      name: "Ticket #",
      selector: (row) => row.ticket_number,
      sortable: true,
      width: "130px",
      cell: (row) => (
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {row.ticket_number}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.ticket_type}
          </Typography>
        </Box>
      ),
    },
    {
      name: "Description",
      selector: (row) => row.short_description,
      sortable: true,
      grow: 2,
      wrap: true,
      cell: (row) => (
        <Box>
          <Typography variant="body2" fontWeight="500" sx={{ mb: 0.5 }}>
            {row.short_description
              ? row.short_description.length > 80
                ? row.short_description.substring(0, 80) + "..."
                : row.short_description
              : "No description"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            <i className="fa fa-calendar-o" style={{ marginRight: "4px" }} />
            {row.opened_at ? new Date(row.opened_at).toLocaleString() : "N/A"}
          </Typography>
        </Box>
      ),
    },
    {
      name: "Priority",
      width: "120px",
      cell: (row) => getPriorityChip(row.priority),
    },
    {
      name: "State",
      width: "120px",
      cell: (row) => getStateChip(row.state),
    },
    {
      name: "Assigned",
      width: "130px",
      cell: (row) => (
        <Typography variant="body2" color="text.primary">
          {row.assigned_to || "Unassigned"}
        </Typography>
      ),
    },
    {
      name: "Status",
      width: "150px",
      cell: (row) => (
        <Box display="flex" gap={0.5} flexWrap="wrap">
          {row.is_analyzed && (
            <Chip label="Analyzed" color="success" size="small" />
          )}
          {row.is_trained && (
            <Chip label="Trained" color="primary" size="small" />
          )}
          {!row.is_analyzed && (
            <Chip label="Not Analyzed" color="default" size="small" />
          )}
        </Box>
      ),
    },
    {
      name: "Actions",
      width: "180px",
      cell: (row) => (
        <Box display="flex" gap={1}>
          {permission.canModify && (
            <Tooltip title="Analyze Ticket" arrow>
              <span>
                <Button
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={() => handleAnalyzeTicket(row)}
                  disabled={loading || isDeleting}
                >
                  <i className="fa fa-bar-chart" />
                </Button>
              </span>
            </Tooltip>
          )}
          {permission.canDelete && (
            <Tooltip title="Delete Ticket" arrow>
              <span>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => handleDeleteTicket(row.id, row.ticket_number)}
                  disabled={loading || isDeleting}
                >
                  <i className="fa fa-trash-o" />
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const ExpandedComponent = ({ data }) => (
    <Box
      sx={{
        p: 2,
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        backgroundColor:
          theme.palette.mode === "dark" ? "#0f172a" : "#f8f9fa",
      }}
    >
      <Box display="flex" flexWrap="wrap" gap={3}>
        <Box flex={1} minWidth="300px">
          <Typography variant="body2" color="text.primary" mb={1}>
            <strong>Ticket ID:</strong> {data.ticket_sys_id}
          </Typography>
          <Typography variant="body2" color="text.primary" mb={1}>
            <strong>Type:</strong> {data.ticket_type}
          </Typography>
          <Typography variant="body2" color="text.primary" mb={1}>
            <strong>Priority:</strong> {data.priority}
          </Typography>
          <Typography variant="body2" color="text.primary" mb={1}>
            <strong>Impact:</strong> {data.impact}
          </Typography>
          <Typography variant="body2" color="text.primary" mb={1}>
            <strong>Urgency:</strong> {data.urgency}
          </Typography>
          <Typography variant="body2" color="text.primary" mb={1}>
            <strong>Category:</strong> {data.category || "N/A"}
          </Typography>
          <Typography variant="body2" color="text.primary" mb={1}>
            <strong>Subcategory:</strong> {data.subcategory || "N/A"}
          </Typography>
        </Box>
        <Box flex={1} minWidth="300px">
          <Typography variant="body2" color="text.primary" mb={1}>
            <strong>Opened:</strong>{" "}
            {data.opened_at ? new Date(data.opened_at).toLocaleString() : "N/A"}
          </Typography>
          <Typography variant="body2" color="text.primary" mb={1}>
            <strong>Resolved:</strong>{" "}
            {data.resolved_at ? new Date(data.resolved_at).toLocaleString() : "N/A"}
          </Typography>
          <Typography variant="body2" color="text.primary" mb={1}>
            <strong>Closed:</strong>{" "}
            {data.closed_at ? new Date(data.closed_at).toLocaleString() : "N/A"}
          </Typography>
          <Typography variant="body2" color="text.primary" mb={1}>
            <strong>Comments:</strong> {data.comments_count || 0}
          </Typography>
          <Typography variant="body2" color="text.primary" mb={1}>
            <strong>Work Notes:</strong> {data.work_notes_count || 0}
          </Typography>
          <Typography variant="body2" color="text.primary" mb={1}>
            <strong>Last Synced:</strong>{" "}
            {data.last_sync_date
              ? new Date(data.last_sync_date).toLocaleString()
              : "Never"}
          </Typography>
        </Box>
      </Box>
      {data.description && (
        <Box mt={2}>
          <Typography variant="body2" fontWeight="bold" mb={1}>
            Full Description:
          </Typography>
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ whiteSpace: "pre-wrap" }}
          >
            {data.description}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Paper
      sx={{
        p: 3,
        mt: 3,
        borderRadius: 3,
        boxShadow: theme.palette.mode === "dark" ? 5 : 2,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" color="text.primary">
          ServiceNow Tickets
        </Typography>
      </Box>

      {!chatbotId && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <i className="fa fa-info-circle" />{" "}
          Please select a chatbot from the main page to sync and manage
          ServiceNow tickets.
        </Alert>
      )}

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        {permission.canModify && (
          <Box display="flex" gap={2} flex={1} minWidth="250px">
            <FormControl size="small" sx={{ minWidth: "180px" }}>
              <InputLabel>Ticket Type</InputLabel>
              <Select
                value={ticketType}
                label="Ticket Type"
                onChange={(e) => handleTicketType(e.target.value)}
                disabled={isSyncing || loading}
              >
                <MenuItem value="incident">Incident</MenuItem>
                <MenuItem value="sc_request">Service Request</MenuItem>
                <MenuItem value="problem">Problem</MenuItem>
                <MenuItem value="change_request">Change Request</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleSyncTickets}
              disabled={isSyncing || loading || !chatbotId}
              startIcon={
                isSyncing ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <i className="fa fa-refresh" />
                )
              }
            >
              {isSyncing ? "Syncing..." : "Sync Tickets"}
            </Button>
          </Box>
        )}
        <TextField
          size="small"
          placeholder="Search by ticket number or description..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ flex: 1, minWidth: "250px" }}
          InputProps={{
            startAdornment: (
              <i
                className="fa fa-search"
                style={{ marginRight: "8px", color: theme.palette.text.secondary }}
              />
            ),
          }}
        />
      </Box>

      {error && (
        <Alert
          severity={
            error.toLowerCase().includes("error") ||
            error.toLowerCase().includes("failed")
              ? "error"
              : "success"
          }
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          <i className="fa fa-exclamation-triangle" /> {error}
        </Alert>
      )}

      {loading && tickets.length === 0 ? (
        <Box display="flex" flexDirection="column" alignItems="center" py={4}>
          <CircularProgress size={30} />
          <Typography mt={2}>Loading tickets...</Typography>
        </Box>
      ) : tickets.length === 0 ? (
        <Box textAlign="center" py={4}>
          <i
            className="fa fa-ticket fa-3x"
            style={{ color: theme.palette.text.disabled }}
          />
          <Typography mt={2} color="text.primary">
            No tickets found
          </Typography>
        </Box>
      ) : (
        <DataTable
          columns={columns}
          data={tickets}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 25, 50]}
          responsive
          highlightOnHover
          pointerOnHover
          theme={theme.palette.mode === "dark" ? "dark" : "default"}
          customStyles={customDataTableStyles}
          expandableRows
          expandableRowsComponent={ExpandedComponent}
        />
      )}

      {/* Ticket Analysis Modal */}
      {isAnalysisModalOpen && selectedTicket && (
        <TicketAnalysisModal
          isOpen={isAnalysisModalOpen}
          onClose={handleModalClose}
          ticket={selectedTicket}
          permission={permission}
        />
      )}
    </Paper>
  );
}